import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Building2, ArrowRight, Loader2, Shield, Clock, FileText, MessageSquare } from 'lucide-react';
import API from '../lib/api';

// ==================== TEMPLATE: MINIMALISTA ====================
function TemplateMinimalista({ portal, navigate }) {
  const color = portal.primary_color || '#FF6B35';

  return (
    <div className="min-h-screen flex flex-col" data-testid="public-portal-page">
      {/* Minimal header */}
      <header className="py-6 px-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {portal.logo_url ? (
            <img src={portal.logo_url} alt="" className="w-8 h-8 object-contain rounded" />
          ) : (
            <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: color }}>
              <Building2 className="w-4 h-4 text-white" />
            </div>
          )}
          <span className="font-semibold text-gray-900 text-sm">{portal.company_name}</span>
        </div>
        <Button
          onClick={() => navigate('/login')}
          variant="outline"
          size="sm"
          className="text-sm border-gray-200 hover:border-gray-300"
          data-testid="portal-access-btn"
        >
          Acceder
        </Button>
      </header>

      {/* Hero - ultra clean */}
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-xl text-center">
          {portal.logo_url ? (
            <img src={portal.logo_url} alt="" className="w-16 h-16 object-contain mx-auto mb-8 rounded-lg" />
          ) : (
            <div className="w-16 h-16 rounded-lg mx-auto mb-8 flex items-center justify-center" style={{ backgroundColor: color }}>
              <Building2 className="w-8 h-8 text-white" />
            </div>
          )}
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 tracking-tight leading-tight mb-6">
            {portal.company_name}
          </h1>
          <p className="text-xl text-gray-500 leading-relaxed mb-10 max-w-md mx-auto">
            {portal.welcome_message}
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="h-12 px-8 text-base font-medium rounded-full text-white shadow-lg"
            style={{ backgroundColor: color }}
            data-testid="portal-main-btn"
          >
            Acceder al Portal
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>

      {/* Sections - horizontal line */}
      {portal.sections?.length > 0 && (
        <section className="py-16 px-8 border-t border-gray-100">
          <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8">
            {portal.sections.map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-2 h-2 rounded-full mx-auto mb-4" style={{ backgroundColor: color }} />
                <h3 className="font-semibold text-gray-900 text-sm mb-2">{s.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{s.content}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-6 px-8 text-center">
        <p className="text-xs text-gray-400">
          {portal.company_name} &middot; Powered by Urcalportal
        </p>
      </footer>
    </div>
  );
}

// ==================== TEMPLATE: CORPORATIVA ====================
function TemplateCorporativa({ portal, navigate }) {
  const color = portal.primary_color || '#FF6B35';
  const hasBg = !!portal.background_image_url;

  return (
    <div className="min-h-screen" data-testid="public-portal-page">
      {/* Top bar */}
      <div className="h-1 w-full" style={{ backgroundColor: color }} />

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {portal.logo_url ? (
              <img src={portal.logo_url} alt="" className="h-10 object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: color }}>
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
            <div>
              <h2 className="font-bold text-gray-900 text-sm">{portal.company_name}</h2>
              <p className="text-xs text-gray-500">Portal de Empleados</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/login')}
            className="text-white text-sm"
            style={{ backgroundColor: color }}
            data-testid="portal-access-btn"
          >
            Iniciar Sesion
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative py-24 sm:py-32"
        style={{
          backgroundColor: hasBg ? '#000' : '#FAFAFA',
          backgroundImage: hasBg ? `url(${portal.background_image_url})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {hasBg && <div className="absolute inset-0 bg-black/60" />}
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <h1
            className="text-4xl sm:text-5xl font-bold mb-4 leading-tight"
            style={{ color: hasBg ? 'white' : '#111827' }}
          >
            {portal.welcome_message || `Bienvenido a ${portal.company_name}`}
          </h1>
          <p
            className="text-lg mb-8"
            style={{ color: hasBg ? 'rgba(255,255,255,0.8)' : '#6B7280' }}
          >
            Accede a tu documentacion, fichajes y comunicados
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="h-12 px-10 text-base font-semibold rounded-md text-white shadow-lg"
            style={{ backgroundColor: color }}
            data-testid="portal-main-btn"
          >
            Acceder al Portal
          </Button>
        </div>
      </section>

      {/* Sections */}
      {portal.sections?.length > 0 && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-12">Nuestros Servicios</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {portal.sections.map((s, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 rounded-md flex items-center justify-center mb-4 text-white font-bold text-lg" style={{ backgroundColor: color }}>
                    {i + 1}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trust bar */}
      <section className="py-12 px-6" style={{ backgroundColor: color + '08' }}>
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { icon: Shield, text: 'Datos seguros' },
            { icon: Clock, text: 'Acceso 24/7' },
            { icon: FileText, text: 'Documentos digitales' },
            { icon: MessageSquare, text: 'Comunicacion directa' }
          ].map((item, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <item.icon className="w-6 h-6" style={{ color }} />
              <span className="text-xs font-medium text-gray-700">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm text-gray-400">
            &copy; 2026 {portal.company_name}. Portal de empleados powered by Urcalportal.
          </p>
        </div>
      </footer>
    </div>
  );
}

// ==================== TEMPLATE: MODERNA ====================
function TemplateModerna({ portal, navigate }) {
  const color = portal.primary_color || '#FF6B35';
  const hasBg = !!portal.background_image_url;

  return (
    <div className="min-h-screen" data-testid="public-portal-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[75vh] flex items-center justify-center overflow-hidden"
        style={{
          backgroundColor: hasBg ? '#000' : color + '08',
          backgroundImage: hasBg ? `url(${portal.background_image_url})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {hasBg && <div className="absolute inset-0 bg-black/50" />}

        {/* Decorative elements */}
        {!hasBg && (
          <>
            <div className="absolute top-10 right-10 w-72 h-72 rounded-full opacity-10" style={{ backgroundColor: color }} />
            <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full opacity-8" style={{ backgroundColor: color }} />
            <div className="absolute top-1/3 left-1/4 w-24 h-24 rounded-full opacity-5" style={{ backgroundColor: color }} />
            <div className="absolute bottom-1/3 right-1/4 w-16 h-16 rounded-full opacity-10" style={{ backgroundColor: color }} />
          </>
        )}

        <div className="relative z-10 text-center px-6 py-12 max-w-2xl mx-auto">
          {/* Logo */}
          {portal.logo_url ? (
            <img src={portal.logo_url} alt={portal.company_name} className="w-24 h-24 object-contain mx-auto mb-6 rounded-2xl shadow-xl" />
          ) : (
            <div className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl" style={{ backgroundColor: color }}>
              <Building2 className="w-10 h-10 text-white" />
            </div>
          )}

          {/* Company name */}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4"
            style={{ color: hasBg ? 'white' : '#1F2937' }}
          >
            {portal.company_name}
          </h1>

          {/* Welcome message */}
          <p
            className="text-lg sm:text-xl mb-8 max-w-lg mx-auto leading-relaxed"
            style={{ color: hasBg ? 'rgba(255,255,255,0.85)' : '#6B7280' }}
          >
            {portal.welcome_message || 'Bienvenido al portal de empleados'}
          </p>

          {/* CTA Button */}
          <Button
            onClick={() => navigate('/login')}
            className="h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-white"
            style={{ backgroundColor: color }}
            data-testid="portal-access-btn"
          >
            Acceder al Portal
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Sections */}
      {portal.sections?.length > 0 && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {portal.sections.map((section, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl border transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
                  style={{ borderColor: color + '30', backgroundColor: color + '05' }}
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-white font-bold"
                    style={{ backgroundColor: color }}
                  >
                    {idx + 1}
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">{section.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{section.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 px-6 border-t" style={{ borderColor: color + '20' }}>
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm text-gray-400">
            Portal de empleados de {portal.company_name} &middot; Powered by{' '}
            <span className="font-semibold" style={{ color }}>Urcalportal</span>
          </p>
        </div>
      </footer>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function PublicPortalPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchPortal();
  }, [slug]);

  const fetchPortal = async () => {
    try {
      const res = await API.get(`/public-portal/${slug}`);
      setPortal(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F4]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F4]">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Portal no encontrado</h1>
          <p className="text-gray-500 mb-6">El enlace no es valido o la empresa no tiene portal publico.</p>
          <Button onClick={() => navigate('/login')} className="bg-orange-500 hover:bg-orange-600 text-white">
            Ir al Login
          </Button>
        </div>
      </div>
    );
  }

  const template = portal.template || 'moderna';

  switch (template) {
    case 'minimalista':
      return <TemplateMinimalista portal={portal} navigate={navigate} />;
    case 'corporativa':
      return <TemplateCorporativa portal={portal} navigate={navigate} />;
    case 'moderna':
    default:
      return <TemplateModerna portal={portal} navigate={navigate} />;
  }
}
